/**
 * EnglishCalendar - English (AD) calendar implementation
 */

import CalendarBase from './CalendarBase.js';

class EnglishCalendar extends CalendarBase {
  constructor(options = {}) {
    super(options);
  }

  /**
   * Get current date in AD
   */
  getCurrentDate() {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate()
    };
  }

  /**
   * Get month names based on language
   */
  getMonthNames() {
    const englishMonths = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const nepaliMonths = [
      'जनवरी', 'फेब्रुअरी', 'मार्च', 'अप्रिल', 'मे', 'जुन',
      'जुलाई', 'अगस्त', 'सेप्टेम्बर', 'अक्टोबर', 'नोभेम्बर', 'डिसेम्बर'
    ];
    
    return this.options.language === 'ne' ? nepaliMonths : englishMonths;
  }

  /**
   * Get weekday names based on language
   */
  getWeekdayNames() {
    const englishWeekdays = [
      'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
    ];
    
    const nepaliWeekdays = [
      'आइतबार', 'सोमबार', 'मंगलबार', 'बुधबार', 'बिहीबार', 'शुक्रबार', 'शनिबार'
    ];
    
    return this.options.language === 'ne' ? nepaliWeekdays : englishWeekdays;
  }

  /**
   * Get days in month
   */
  getDaysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
  }

  /**
   * Get first day of month (0-6, where 0 is Sunday)
   */
  getFirstDayOfMonth(year, month) {
    return new Date(year, month - 1, 1).getDay();
  }

  /**
   * Format date
   */
  formatDate(date, format = 'YYYY-MM-DD') {
    const year = String(date.year).padStart(4, '0');
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    
    let formattedDate = format
      .replace('YYYY', year)
      .replace('MM', month)
      .replace('DD', day);
    
    if (this.options.language === 'ne') {
      // Convert to Nepali digits
      return this.toNepaliDigits(formattedDate);
    }
    
    return formattedDate;
  }

  /**
   * Convert to Nepali digits
   */
  toNepaliDigits(text) {
    const nepaliDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    return String(text).replace(/\d/g, digit => nepaliDigits[digit]);
  }

  /**
   * Get day of week for a date
   * @protected
   */
  _getDayOfWeek(date) {
    return new Date(date.year, date.month - 1, date.day).getDay();
  }

  /**
   * Create days grid
   * @protected
   */
  _createDaysGrid() {
    const daysGrid = document.createElement('div');
    daysGrid.className = 'ndtp-days-grid';

    const year = this.viewDate.year;
    const month = this.viewDate.month;
    const daysInMonth = this.getDaysInMonth(year, month);
    const firstDayOfMonth = this.getFirstDayOfMonth(year, month);
    
    // Adjust for week start
    let startOffset = firstDayOfMonth - this.options.weekStart;
    if (startOffset < 0) startOffset += 7;

    // Previous month days
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const daysInPrevMonth = this.getDaysInMonth(prevYear, prevMonth);

    for (let i = startOffset - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const date = { year: prevYear, month: prevMonth, day };
      const cell = this._createDayCell(date, false);
      daysGrid.appendChild(cell);
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = { year, month, day };
      const cell = this._createDayCell(date, true);
      daysGrid.appendChild(cell);
    }

    // Next month days
    const totalCells = daysGrid.children.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    
    if (remainingCells > 0) {
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      
      for (let day = 1; day <= remainingCells; day++) {
        const date = { year: nextYear, month: nextMonth, day };
        const cell = this._createDayCell(date, false);
        daysGrid.appendChild(cell);
      }
    }

    return daysGrid;
  }

  /**
   * Create day cell with Nepali digits if language is 'ne'
   * @protected
   */
  _createDayCell(date, isCurrentMonth = true) {
    const cell = super._createDayCell(date, isCurrentMonth);
    
    if (this.options.language === 'ne') {
      cell.textContent = this.toNepaliDigits(date.day);
    }
    
    return cell;
  }

  /**
   * Create year select with appropriate range
   * @protected
   */
  _createYearSelect() {
    const select = document.createElement('select');
    select.className = 'ndtp-year-select';
    
    const currentYear = this.viewDate.year;
    const startYear = currentYear - 100;
    const endYear = currentYear + 100;
    
    for (let year = startYear; year <= endYear; year++) {
      const option = document.createElement('option');
      option.value = year;
      option.textContent = this.options.language === 'ne' 
        ? this.toNepaliDigits(year)
        : year;
      
      if (year === currentYear) {
        option.selected = true;
      }
      select.appendChild(option);
    }
    
    select.onchange = (e) => {
      this.viewDate.year = parseInt(e.target.value, 10);
      this._render();
      this._triggerChange();
    };
    
    return select;
  }

  /**
   * Parse date string to date object
   */
  parseDate(dateString, format = 'YYYY-MM-DD') {
    // If in Nepali, convert digits first
    if (this.options.language === 'ne') {
      dateString = dateString.replace(/[०-९]/g, digit => {
        const nepaliDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
        return nepaliDigits.indexOf(digit);
      });
    }
    
    // Parse based on format
    let year, month, day;
    
    if (format === 'YYYY-MM-DD') {
      const parts = dateString.split('-');
      if (parts.length !== 3) return null;
      
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else if (format === 'YYYY/MM/DD') {
      const parts = dateString.split('/');
      if (parts.length !== 3) return null;
      
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else if (format === 'DD-MM-YYYY') {
      const parts = dateString.split('-');
      if (parts.length !== 3) return null;
      
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else if (format === 'DD/MM/YYYY') {
      const parts = dateString.split('/');
      if (parts.length !== 3) return null;
      
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else if (format === 'MM-DD-YYYY') {
      const parts = dateString.split('-');
      if (parts.length !== 3) return null;
      
      month = parseInt(parts[0], 10);
      day = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else if (format === 'MM/DD/YYYY') {
      const parts = dateString.split('/');
      if (parts.length !== 3) return null;
      
      month = parseInt(parts[0], 10);
      day = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else {
      return null;
    }
    
    // Validate the parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }
    
    return this.isValidDate({ year, month, day }) ? { year, month, day } : null;
  }

  /**
   * Check if a date is valid
   */
  isValidDate(date) {
    if (!date || typeof date !== 'object') {
      return false;
    }
    
    const { year, month, day } = date;
    
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
      return false;
    }
    
    if (month < 1 || month > 12) {
      return false;
    }
    
    const daysInMonth = this.getDaysInMonth(year, month);
    if (day < 1 || day > daysInMonth) {
      return false;
    }
    
    return true;
  }

  /**
   * Add days to a date
   */
  addDays(date, days) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    const jsDate = new Date(date.year, date.month - 1, date.day);
    jsDate.setDate(jsDate.getDate() + days);
    
    return {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate()
    };
  }

  /**
   * Add months to a date
   */
  addMonths(date, months) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    const jsDate = new Date(date.year, date.month - 1, date.day);
    jsDate.setMonth(jsDate.getMonth() + months);
    
    // Handle end of month edge cases
    if (jsDate.getDate() !== date.day) {
      jsDate.setDate(0); // Go to last day of previous month
    }
    
    return {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate()
    };
  }

  /**
   * Add years to a date
   */
  addYears(date, years) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    const jsDate = new Date(date.year, date.month - 1, date.day);
    jsDate.setFullYear(jsDate.getFullYear() + years);
    
    // Handle February 29th edge case
    if (jsDate.getDate() !== date.day) {
      jsDate.setDate(0); // Go to last day of previous month
    }
    
    return {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate()
    };
  }

  /**
   * Get the difference between two dates in days
   */
  getDaysDifference(date1, date2) {
    if (!this.isValidDate(date1) || !this.isValidDate(date2)) {
      throw new Error('Invalid date');
    }
    
    const jsDate1 = new Date(date1.year, date1.month - 1, date1.day);
    const jsDate2 = new Date(date2.year, date2.month - 1, date2.day);
    
    const diffTime = jsDate2.getTime() - jsDate1.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Get start of week for a given date
   */
  getStartOfWeek(date) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    const jsDate = new Date(date.year, date.month - 1, date.day);
    const dayOfWeek = jsDate.getDay();
    const daysToSubtract = (dayOfWeek - this.options.weekStart + 7) % 7;
    
    jsDate.setDate(jsDate.getDate() - daysToSubtract);
    
    return {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate()
    };
  }

  /**
   * Get end of week for a given date
   */
  getEndOfWeek(date) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    const jsDate = new Date(date.year, date.month - 1, date.day);
    const dayOfWeek = jsDate.getDay();
    const daysToAdd = (6 - dayOfWeek + this.options.weekStart) % 7;
    
    jsDate.setDate(jsDate.getDate() + daysToAdd);
    
    return {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate()
    };
  }

  /**
   * Get start of month
   */
  getStartOfMonth(date) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    return { year: date.year, month: date.month, day: 1 };
  }

  /**
   * Get end of month
   */
  getEndOfMonth(date) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    const daysInMonth = this.getDaysInMonth(date.year, date.month);
    return { year: date.year, month: date.month, day: daysInMonth };
  }

  /**
   * Get start of year
   */
  getStartOfYear(date) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    return { year: date.year, month: 1, day: 1 };
  }

  /**
   * Get end of year
   */
  getEndOfYear(date) {
    if (!this.isValidDate(date)) {
      throw new Error('Invalid date');
    }
    
    return { year: date.year, month: 12, day: 31 };
  }
}

export default EnglishCalendar;